import PropTypes from "prop-types";
import {
    Box,
    Typography,
    Divider,
    List,
    ListItem,
    ListItemText,
    Tooltip,
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
    CircularProgress,
    IconButton,
} from "@mui/material";

import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {useEffect, useState} from "react";
import { Info, FileText, Calendar, Languages, FileSearch2, BookType, CloudDownload  } from "lucide-react"
import GTranslateIcon from '@mui/icons-material/GTranslate';
import {useSoftUIController} from "../../../context";
import {useTranslation} from "react-i18next";


export const DRAWER_WIDTH = 280;

export default function EditorSidenav({
                                          documentName,
                                          scannedPages,
                                          sourceLang,
                                          targetLang,
                                          docId,
                                          digitDate,
                                          onToggleDownload,
                                          docData,
                                          selectedFields,
                                          handleFieldChange,
                                      }) {

    const cleanedDate = new Date(digitDate).toLocaleDateString();
    const [isDownloading, setIsDownloading] = useState(false);
    const {i18n} = useTranslation();
    const { t } = useTranslation(['editor']);
    const [controller] = useSoftUIController();
    const { direction} = controller;

    useEffect(() => {
        if(isDownloading){
            setIsDownloading(false);
            onToggleDownload()
        }
    }, [isDownloading]);

    const handleDownloadClick = () => {
        setIsDownloading(true);
    };


    return (
        <Box
            sx={{
                width: DRAWER_WIDTH,
                p: 2,
                backgroundColor: "white",
                boxShadow: "0px 2px 10px rgba(0,0,0,0.05)",
                fontFamily: "Inter, sans-serif",
                direction: i18n.language === "ar" ? "rtl" : "ltr",
            }}
        >
            {/* About Section */}
            <Box
                sx={{
                    backgroundColor: "#fff",
                    borderRadius: 2,
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
                    p: 2.5,
                    mb: 3,
                    fontFamily: "Inter, sans-serif",
                    direction: i18n.language === "ar" ? "rtl" : "ltr",
                }}
            >
                <Box display="flex" alignItems="center" mb={2}>
                    <Info style={{ width: 18, height: 18, color: "#6b7280" }} />
                    <Typography
                        sx={{
                            fontSize: "1rem",
                            fontWeight: 400,
                            color: "#1f2937",
                            ...(i18n.language === "ar" ? { mr: 1 } : { ml: 1 }),
                        }}
                    >
                        {t("about_this_item") || "About"}
                    </Typography>
                </Box>

                <Divider sx={{ mb: 2, borderColor: "#e5e7eb" }} />

                <List disablePadding>
                    <ListItem disableGutters sx={{ mb: 1 }}>
                        <Box
                            sx={{
                                width: 30,
                                height: 30,
                                bgcolor: "#e9ecef",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                ...(i18n.language === "ar" ? { ml: 1.5 } : { mr: 1.5 }),
                            }}
                        >
                            <BookType style={{ color: "#6b7280", width: 16, height: 16 }} />
                        </Box>
                        <Tooltip title={documentName}>
                            <ListItemText
                                primary={`${t("title") || "Title"}: ${documentName?.substring(0, 30)}${documentName?.length > 30 ? "…" : ""}`}
                                primaryTypographyProps={{
                                    fontSize: "0.875rem",
                                    color: "#374151",
                                    textAlign: i18n.language === "ar" ? "right" : "left"
                                }}
                            />
                        </Tooltip>
                    </ListItem>

                    <ListItem disableGutters sx={{ mb: 1 }}>
                        <Box
                            sx={{
                                width: 30,
                                height: 30,
                                bgcolor: "#e9ecef",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                ...(i18n.language === "ar" ? { ml: 1.5 } : { mr: 1.5 }),
                            }}
                        >
                            <FileSearch2 style={{ color: "#6b7280", width: 16, height: 16 }} />
                        </Box>
                        <ListItemText
                            primary={`${t("id") || "ID"}: ${docId}`}
                            primaryTypographyProps={{
                                fontSize: "0.875rem",
                                color: "#374151",
                                textAlign: i18n.language === "ar" ? "right" : "left"
                            }}
                        />
                    </ListItem>

                    <ListItem disableGutters sx={{ mb: 1 }}>
                        <Box
                            sx={{
                                width: 30,
                                height: 30,
                                bgcolor: "#e9ecef",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                ...(i18n.language === "ar" ? { ml: 1.5 } : { mr: 1.5 }),
                            }}
                        >
                            <FileText style={{ color: "#6b7280", width: 16, height: 16 }} />
                        </Box>
                        <ListItemText
                            primary={`${t("pages") || "Pages"}: ${scannedPages}`}
                            primaryTypographyProps={{
                                fontSize: "0.875rem",
                                color: "#374151",
                                textAlign: i18n.language === "ar" ? "right" : "left"
                            }}
                        />
                    </ListItem>

                    <ListItem disableGutters sx={{ mb: 1 }}>
                        <Box
                            sx={{
                                width: 30,
                                height: 30,
                                bgcolor: "#e9ecef",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                ...(i18n.language === "ar" ? { ml: 1.5 } : { mr: 1.5 }),
                            }}
                        >
                            <Languages style={{ color: "#6b7280", width: 16, height: 16 }} />
                        </Box>
                        <ListItemText
                            primary={`${t("source") || "Source"}: ${sourceLang}`}
                            primaryTypographyProps={{
                                fontSize: "0.875rem",
                                color: "#374151",
                                textAlign: i18n.language === "ar" ? "right" : "left",
                            }}
                        />
                    </ListItem>

                    <ListItem disableGutters sx={{ mb: 1 }}>
                        <Box
                            sx={{
                                width: 30,
                                height: 30,
                                bgcolor: "#e9ecef",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                ...(i18n.language === "ar" ? { ml: 1.5 } : { mr: 1.5 }),
                            }}
                        >
                            <GTranslateIcon sx={{ color: "#6b7280", width: 16, height: 16 }} />
                        </Box>
                        <ListItemText
                            primary={`${t("target") || "Target"}: ${targetLang}`}
                            primaryTypographyProps={{
                                fontSize: "0.875rem",
                                color: "#374151",
                                textAlign: i18n.language === "ar" ? "right" : "left",
                            }}
                        />
                    </ListItem>


                    <ListItem disableGutters>
                        <Box
                            sx={{
                                width: 30,
                                height: 30,
                                bgcolor: "#e9ecef",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                ...(i18n.language === "ar" ? { ml: 1.5 } : { mr: 1.5 }),
                            }}
                        >
                            <Calendar style={{ color: "#6b7280", width: 16, height: 16 }} />
                        </Box>
                        <ListItemText
                            primary={`${t("date") || "Date"}: ${cleanedDate}`}
                            primaryTypographyProps={{
                                fontSize: "0.875rem",
                                color: "#374151",
                                textAlign: i18n.language === "ar" ? "right" : "left"
                            }}
                        />
                    </ListItem>
                </List>
            </Box>

            {/* Download Options */}
            <Box
                sx={{
                    backgroundColor: "#fff",
                    borderRadius: 2,
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
                    p: 2.5,
                }}
            >
                <Box display="flex" alignItems="center" mb={2}>
                    <CloudDownload style={{ width: 18, height: 18, color: "#6b7280" }} />
                    <Typography
                        sx={{
                            fontSize: "1rem",
                            fontWeight: 400,
                            color: "#1f2937",
                            ...(i18n.language === "ar" ? { mr: 1 } : { ml: 1 }),
                        }}
                    >
                        {t("download_options") || "Download"}
                    </Typography>
                </Box>
                <Divider sx={{ mb: 2, borderColor: "#e5e7eb" }} />

                <Box sx={{ mb: 2 }}>
                    <FormGroup>
                        {docData?.map((digitization_result) => (
                            <FormControlLabel
                                key={digitization_result}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={selectedFields[digitization_result]}
                                        onChange={() => handleFieldChange(digitization_result)}
                                        color="secondary"
                                        sx={{
                                            mr: i18n.language === 'ar' ? 0 : 0.5,
                                            ml: i18n.language === 'ar' ? 0.5 : 0,
                                        }}

                                    />
                                }
                                label={t(digitization_result)}
                                sx={{
                                    ...(i18n.language === "ar" ? { mr: 1 } : { ml: 1 }),
                                    alignItems: "center",
                                    mb: 1,
                                    "& .MuiFormControlLabel-label": {
                                        fontSize: "0.85rem",
                                        color: "#374151",
                                    },
                                }}
                            />
                        ))}
                    </FormGroup>
                </Box>

                <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleDownloadClick}
                    disabled={Object.values(selectedFields).every(value => !value) || isDownloading}
                    startIcon={
                        isDownloading ? (
                            <CircularProgress size={18} color="inherit" />
                        ) : (
                            <FileDownloadIcon />
                        )
                    }
                    sx={{
                        borderColor: "#374151",
                        color: "#6b7280",
                        textTransform: "none",
                        borderRadius: "8px",
                        py: 1,
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        transition: "all 0.2s ease-in-out",
                        '&:hover': {
                            backgroundColor: "#374151",
                            color: "#fff",
                            borderColor: "#374151"
                        },
                    }}
                >
                    {isDownloading ? t("processing") || "Processing..." : t("download_selected") || "Download Selected"}
                </Button>
            </Box>

        </Box>
    );
}


EditorSidenav.propTypes = {
    documentName: PropTypes.string,
    scannedPages: PropTypes.number,
    sourceLang: PropTypes.string,
    targetLang: PropTypes.string,
    docId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    digitDate: PropTypes.string,
    downloads: PropTypes.object,
    onToggleDownload: PropTypes.func,
    onDownload: PropTypes.func,
    onDelete: PropTypes.func,
    onManualDownload: PropTypes.func,
    status: PropTypes.array,
    docData: PropTypes.array,
    selectedFields: PropTypes.object,
    handleFieldChange: PropTypes.func,
};